import React, { useState } from 'react';
import axios from 'axios';
import {
  FaChartLine, FaHistory, FaStar, FaUnlock, FaChevronDown,
} from 'react-icons/fa';

const DEFAULT_USA_FORMATION_STEPS = [
  { step: 1, title: 'Company formation checklist', status: 'in-progress' },
  { step: 2, title: 'Post Formation documents', status: 'pending' },
  { step: 3, title: 'Tax ID (EIN)', status: 'pending' },
];

const formatUSAFormationStatusLabel = (status) => {
  if (status === 'in-progress') return 'IN PROGRESS';
  if (status === 'submitted') return 'COMPLETED';
  return 'PENDING';
};

const formatUSAFormationStatusShort = (status) => {
  if (status === 'in-progress') return 'In Progress';
  if (status === 'submitted') return 'Completed';
  return 'Pending';
};

const formatDetailedDateTime = (dateString) => {
  if (!dateString) return <span className="text-gray-400 italic">Not yet recorded</span>;
  const date = new Date(dateString);
  return (
    <div className="flex flex-col">
      <span className="font-semibold text-gray-900 dark:text-white">
        {date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </span>
      <span className="text-sm text-gray-600 dark:text-gray-400">
        {date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
      </span>
    </div>
  );
};

const UsaFormationTab = ({
  selectedMenuUser,
  setSelectedMenuUser,
  updateClientFromUser,
}) => {
  const [updatingUSAFormationStep, setUpdatingUSAFormationStep] = useState({});

  const getUSAFormationStepsList = () => {
    const raw = selectedMenuUser?.companySteps;
    if (!raw?.length) return DEFAULT_USA_FORMATION_STEPS;
    const plainSteps = raw.map((step) => (
      typeof step.toObject === 'function' ? step.toObject() : step
    ));
    return DEFAULT_USA_FORMATION_STEPS.map((ds) => {
      const existing = plainSteps.find((s) => s.step === ds.step);
      return existing
        ? { ...ds, ...existing, title: existing.title || ds.title, status: existing.status || ds.status }
        : ds;
    });
  };

  const handleUpdateUSAFormationStepStatus = async (stepNumber, status) => {
    setUpdatingUSAFormationStep((prev) => ({ ...prev, [stepNumber]: true }));
    try {
      const token = localStorage.getItem('employeeToken');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/usa-company-formation/admin/update-step-status`,
        {
          userId: selectedMenuUser._id,
          stepNumber,
          status,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.user) {
        setSelectedMenuUser(response.data.user);
        updateClientFromUser?.(response.data.user);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update USA formation step status.');
    } finally {
      setUpdatingUSAFormationStep((prev) => ({ ...prev, [stepNumber]: false }));
    }
  };

  const renderUSAFormationActivityReport = () => {
    if (!selectedMenuUser) return null;

    const stepsList = getUSAFormationStepsList();
    const completedCount = stepsList.filter((s) => s.status === 'submitted').length;
    const inProgressStep = stepsList.find((s) => s.status === 'in-progress');
    const formationStatus = completedCount === 3
      ? 'Completed'
      : inProgressStep
        ? `Step ${inProgressStep.step} In Progress`
        : 'Not Started';

    return (
      <div className="mt-8 pt-8 border-t border-gray-200 dark:border-neutral-700">
        <h5 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <FaHistory className="text-primary-500" /> USA Company Formation Activity Report
        </h5>
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-5 border border-amber-200 dark:border-amber-800">
          <h6 className="font-bold text-lg text-amber-900 dark:text-amber-300 mb-4 flex items-center gap-2">
            <FaStar /> {selectedMenuUser.name || 'Client'}
          </h6>
          <div className="space-y-4">
            <div className="bg-white dark:bg-neutral-800 rounded-lg p-4 shadow-sm max-w-md">
              <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Formation Started</h6>
              {formatDetailedDateTime(selectedMenuUser.usaCompanyFormation?.createdAt)}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {stepsList.map((step) => (
                <div key={step.step} className="bg-white dark:bg-neutral-800 rounded-lg p-4 shadow-sm">
                  <h6 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Step {step.step}: {step.title}
                  </h6>
                  <p className="text-[10px] font-bold uppercase text-primary-600 mb-2">
                    {formatUSAFormationStatusShort(step.status)}
                  </p>
                  {step.step === 1 && step.status === 'in-progress' && typeof step.progress === 'number' && (
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-2">
                      Checklist progress: {step.progress}%
                    </p>
                  )}
                  <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 mb-1">Completed At</p>
                  {formatDetailedDateTime(step.submittedAt)}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-5 border border-purple-200 dark:border-purple-800">
          <h6 className="font-bold text-lg text-purple-900 dark:text-purple-300 mb-4">Activity Summary</h6>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-neutral-800 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-primary-600">
                {selectedMenuUser.isUSAEnabled ? '✓' : '✗'}
              </div>
              <div className="text-[10px] text-gray-600 dark:text-gray-400 mt-1 uppercase font-bold">Service Active</div>
            </div>
            <div className="bg-white dark:bg-neutral-800 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-600">{completedCount}/3</div>
              <div className="text-[10px] text-gray-600 dark:text-gray-400 mt-1 uppercase font-bold">Steps Completed</div>
            </div>
            <div className="bg-white dark:bg-neutral-800 rounded-lg p-3 text-center">
              <div className="text-lg font-bold text-blue-600 uppercase">
                {selectedMenuUser.assignedCompanyForm || 'LLC'}
              </div>
              <div className="text-[10px] text-gray-600 dark:text-gray-400 mt-1 uppercase font-bold">Entity Type</div>
            </div>
            <div className="bg-white dark:bg-neutral-800 rounded-lg p-3 text-center">
              <div className="text-sm font-bold text-orange-600">{formationStatus}</div>
              <div className="text-[10px] text-gray-600 dark:text-gray-400 mt-1 uppercase font-bold">Formation Status</div>
            </div>
          </div>
          {selectedMenuUser.usaFeedback?.rating && (
            <div className="mt-4 bg-white dark:bg-neutral-800 rounded-lg p-4 shadow-sm">
              <h6 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Client Feedback</h6>
              <p className="text-sm text-gray-900 dark:text-white">
                {'★'.repeat(selectedMenuUser.usaFeedback.rating)}{'☆'.repeat(5 - selectedMenuUser.usaFeedback.rating)}
                {selectedMenuUser.usaFeedback.comment && (
                  <span className="block mt-1 text-xs text-gray-500 dark:text-gray-400">{selectedMenuUser.usaFeedback.comment}</span>
                )}
              </p>
              {selectedMenuUser.usaFeedback.submittedAt && (
                <p className="text-[10px] text-gray-400 mt-2">
                  Submitted {new Date(selectedMenuUser.usaFeedback.submittedAt).toLocaleDateString()}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <h5 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <FaChartLine className="text-primary-500" /> USA Company Formation Steps Status
      </h5>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {getUSAFormationStepsList().map((step) => {
          const isUpdating = updatingUSAFormationStep[step.step];
          return (
            <div
              key={step.step}
              className={`p-4 rounded-xl border-2 transition-all ${
                step.status === 'submitted'
                  ? 'bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800'
                  : step.status === 'in-progress'
                    ? 'bg-amber-50/50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800'
                    : 'bg-gray-50/50 dark:bg-neutral-900/50 border-gray-200 dark:border-neutral-700'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold text-gray-400 uppercase">Step {step.step}</span>
                <span className={`px-2 py-0.5 text-[9px] font-black rounded-full uppercase tracking-wider ${
                  step.status === 'submitted'
                    ? 'bg-blue-500 text-white'
                    : step.status === 'in-progress'
                      ? 'bg-amber-500 text-white'
                      : 'bg-gray-500 text-white'
                }`}>
                  {formatUSAFormationStatusLabel(step.status)}
                </span>
              </div>

              <h6 className="font-bold text-gray-900 dark:text-white text-xs mb-1.5">{step.title}</h6>

              <div className="relative mt-3">
                <label className="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">
                  Update Status
                </label>
                <div className="relative">
                  <select
                    value={step.status || 'pending'}
                    onChange={(e) => handleUpdateUSAFormationStepStatus(step.step, e.target.value)}
                    disabled={isUpdating}
                    className="w-full pl-3 pr-8 py-2 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl text-xs font-semibold text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 outline-none transition-all appearance-none cursor-pointer"
                  >
                    <option value="pending">Pending</option>
                    <option value="in-progress">In Progress</option>
                    <option value="submitted">Submitted / Completed</option>
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                    {isUpdating ? (
                      <div className="w-3.5 h-3.5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <FaChevronDown className="w-2.5 h-2.5" />
                    )}
                  </div>
                </div>
              </div>

              {step.status === 'submitted' && (
                <button
                  type="button"
                  onClick={() => handleUpdateUSAFormationStepStatus(step.step, 'in-progress')}
                  disabled={isUpdating}
                  className="w-full mt-2 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-700 text-[10px] font-bold rounded-lg transition-all flex items-center justify-center gap-1.5"
                >
                  <FaUnlock className="w-3 h-3" /> Re-open Step {step.step}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {renderUSAFormationActivityReport()}
    </div>
  );
};

export default UsaFormationTab;
